import { useState } from "react";
import { Loader2, Send, Briefcase, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { reachGoal } from "@/lib/metrika";

interface DemoRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DemoRequestDialog({ open, onOpenChange }: DemoRequestDialogProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [volume, setVolume] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !company.trim()) {
      toast.error("Заполните имя, email и компанию");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Укажите корректный email");
      return;
    }
    setSending(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const message = [
        "[DEMO_AGENCY]",
        `Компания: ${company.trim()}`,
        `Объём креативов: ${volume.trim() || "не указан"}`,
      ].join("\n");
      const { data, error } = await supabase.functions.invoke("submit-contact", {
        body: { name: name.trim(), email: email.trim(), message },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      reachGoal("demo_request_agency_sent");
      setDone(true);
    } catch (err: any) {
      toast.error(err?.message || "Не удалось отправить заявку. Попробуйте позже.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) {
          setTimeout(() => {
            setDone(false);
            setName("");
            setEmail("");
            setCompany("");
            setVolume("");
          }, 200);
        }
      }}
    >
      <DialogContent className="max-w-md rounded-3xl">
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/15">
            <Briefcase className="h-5 w-5 text-primary" />
          </div>
          <DialogTitle className="text-xl font-extrabold">Демо для агентств</DialogTitle>
          <DialogDescription>
            Покажем, как ERIDON автоматизирует маркировку для всех ваших клиентов. Ответим в течение рабочего дня.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <CheckCircle2 className="h-12 w-12 text-success" />
            <p className="text-base font-semibold text-foreground">Заявка отправлена</p>
            <p className="text-sm text-muted-foreground">
              Менеджер свяжется с вами по email <span className="font-medium text-foreground">{email}</span>.
            </p>
            <Button onClick={() => onOpenChange(false)} className="mt-2 w-full rounded-xl">
              Закрыть
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-2 space-y-3">
            <Input
              placeholder="Ваше имя *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              required
            />
            <Input
              type="email"
              placeholder="Рабочий email *"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={200}
              required
            />
            <Input
              placeholder="Агентство / компания *"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              maxLength={150}
              required
            />
            <Textarea
              placeholder="Сколько креативов в месяц / какие ОРД сейчас используете"
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
              maxLength={500}
              rows={3}
              className="resize-none"
            />
            <Button type="submit" disabled={sending} className="w-full gap-2 rounded-xl glow-cta min-h-[48px]">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Отправка…" : "Заказать демо"}
            </Button>
            <p className="text-center text-[11px] text-muted-foreground">
              Нажимая кнопку, вы соглашаетесь с{" "}
              <a href="/privacy" className="underline hover:text-foreground">политикой конфиденциальности</a>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
